import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaTasks, FaPlus, FaColumns, FaList, FaCog, FaArchive } from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import usePersistedFilters from "../../../Hooks/usePersistedFilters";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetTasksQuery,
  useGetTaskBoardQuery,
  useGetTaskProjectsQuery,
  useGetTaskLabelsQuery,
  useGetTaskAssigneesQuery,
  useMoveTaskMutation,
} from "../../../store";
import {
  PageShell,
  PageHeader,
  FilterTabs,
  SearchInput,
  EmptyState,
  ListSkeleton,
  MascotLoadingOverlay,
} from "../../../Components/shared";
import { CustomDropdown, Checkbox } from "../../../Components/atoms";
import TaskList, { PAGE_SIZE } from "../../../Components/tasks/TaskList";
import TaskBoard from "../../../Components/tasks/TaskBoard";
import TaskDetailDrawer from "../../../Components/tasks/TaskDetailDrawer";
import NewTaskModal from "../../../Components/tasks/NewTaskModal";
import TaskSettings from "../../../Components/tasks/TaskSettings";
import { PRIORITIES, TYPES } from "../../../Components/tasks/taskConstants";

// İş Planlama — başlık, araç çubuğu, filtreler, pano/liste/ayarlar sekmeleri.
// Contract: BayinerIOT-Workspace-/docs/api-contracts/tasks.md

const DEFAULT_FILTERS = {
  view: "list",
  project: "",
  assignee: [],
  priority: [],
  type: [],
  labels: [],
  search: "",
  mine: false,
  includeCancelled: false,
  page: 1,
  sort: { field: "updatedAt", order: "desc" },
};

const ALL_PROJECTS = { id: "all", label: "Tüm projeler" };

const toOptions = (list, idKey = "id") => list.map((x) => ({ id: x[idKey], label: x.label }));
const pick = (options, ids) => options.filter((o) => ids.includes(o.id));

export default function TasksPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { hasPermission } = usePermission();
  const { showWarning, showError } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedKey = searchParams.get("is") || "";

  const [filters, setFilters] = usePersistedFilters("tasks", DEFAULT_FILTERS);
  const [newOpen, setNewOpen] = useState(false);
  const [searchText, setSearchText] = useState(filters.search);

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, page: 1, ...patch }));

  // Arama: yazarken 300 ms bekle, sonra filtreye yaz
  useEffect(() => {
    if (searchText === filters.search) return undefined;
    const t = setTimeout(() => setFilter({ search: searchText }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const { data: projectsData, isSuccess: projectsLoaded } = useGetTaskProjectsQuery({ token });
  const { data: labelsData } = useGetTaskLabelsQuery({ token });
  const { data: assigneesData } = useGetTaskAssigneesQuery({ token });
  const projects = useMemo(() => projectsData?.data?.projects || [], [projectsData]);

  // Kalıcı filtredeki proje artık listede yoksa (arşiv/silinme/yetki) sıfırla;
  // yüklenene kadar ve sıfırlanana kadar sorgulara bayat id gitmez.
  const projectStale =
    Boolean(filters.project) && projectsLoaded && !projects.some((p) => p._id === filters.project);
  const activeProject = projectStale ? "" : filters.project;
  useEffect(() => {
    if (projectStale) setFilter({ project: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectStale]);

  const canCreate = projects.some((p) => p.can?.createTask);
  const canSettings = hasPermission("tasks.admin") || projects.some((p) => p.myRole === "manager");
  const view =
    filters.view === "settings" && !canSettings ? "list" : filters.view;

  const projectOptions = useMemo(
    () => projects.map((p) => ({ id: p._id, label: `${p.key} · ${p.name}` })),
    [projects],
  );
  const assigneeOptions = useMemo(
    () => [
      { id: "unassigned", label: "Atanmamış" },
      ...(assigneesData?.data?.users || []).map((u) => ({
        id: u._id,
        label: `${u.name || ""} ${u.lastName || ""}`.trim(),
      })),
    ],
    [assigneesData],
  );
  const labelOptions = useMemo(
    () => (labelsData?.data?.labels || []).map((l) => ({ id: l._id, label: l.name })),
    [labelsData],
  );
  const priorityOptions = useMemo(() => toOptions(PRIORITIES), []);
  const typeOptions = useMemo(() => toOptions(TYPES), []);

  const filterArgs = {
    ...(filters.assignee.length ? { assignee: filters.assignee } : {}),
    ...(filters.priority.length ? { priority: filters.priority } : {}),
    ...(filters.type.length ? { type: filters.type } : {}),
    ...(filters.labels.length ? { labels: filters.labels } : {}),
    ...(filters.search ? { search: filters.search } : {}),
    mine: filters.mine,
    includeCancelled: filters.includeCancelled,
  };
  // 1.7 — Arşiv sekmesi: yalnız arşivdeki işler (iptal edilip arşivlenenler dahil), liste düzeninde.
  const archiveMode = view === "archive";
  if (archiveMode) {
    filterArgs.archived = true;
    filterArgs.includeCancelled = true;
  }

  // list her sekmede çekilir (başlık istatistikleri); pano/ayarlarda yalnız stats için limit 1
  const listMode = view === "list" || archiveMode;
  const listQuery = useGetTasksQuery({
    token,
    project: activeProject || null,
    ...filterArgs,
    sort: filters.sort,
    page: listMode ? filters.page : 1,
    limit: listMode ? PAGE_SIZE : 1,
    // 1.6 — listede alt işler üst işin altında (yalnız kök satırlar sayfalanır)
    ...(listMode ? { tree: true } : {}),
  });
  // Proje seçili değilse "tüm projeler" panosu (contract 1.3): project null gider,
  // sunucu liste ile aynı görme kuralını uygular ve ordered: false döner.
  const boardQuery = useGetTaskBoardQuery(
    { token, project: activeProject || null, ...filterArgs },
    { skip: view !== "board" },
  );
  const [moveTask] = useMoveTaskMutation();

  const listData = listQuery.data?.data;
  const stats = listData?.stats;

  // `subtasks`: üst işle birlikte taşınacak alt işler (TaskBoard). Üst iş
  // taşındıktan sonra sırayla gönderilir — her biri bir öncekinin altına yerleşir.
  const onMove = async ({ id, version, status, beforeId, afterId, subtasks = [] }) => {
    try {
      await moveTask({ token, id, version, status, beforeId, afterId }).unwrap();
      for (const sub of subtasks) {
        // eslint-disable-next-line no-await-in-loop
        await moveTask({ token, ...sub }).unwrap();
      }
    } catch (err) {
      if (err?.status === 409) {
        showWarning("İş bu arada değişti, pano yenilendi.");
        boardQuery.refetch();
      } else {
        showError(err?.data?.message || "İş taşınamadı.");
      }
    }
  };

  const openTask = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("is", key);
    setSearchParams(next);
  };
  const closeTask = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("is");
    setSearchParams(next);
  };

  const tabs = [
    { id: "board", label: "Pano", icon: FaColumns },
    { id: "list", label: "Liste", icon: FaList },
    { id: "archive", label: "Arşiv", icon: FaArchive },
    ...(canSettings ? [{ id: "settings", label: "Ayarlar", icon: FaCog }] : []),
  ];

  const headerStats = [
    { label: "Açık iş", value: stats?.open ?? 0, color: "indigo" },
    { label: "Bana atanan", value: stats?.assignedToMe ?? 0, color: "blue" },
    { label: "Geciken", value: stats?.overdue ?? 0, color: "red" },
    { label: "Bu hafta biten", value: stats?.doneThisWeek ?? 0, color: "emerald" },
  ];

  const errorNode = (
    <EmptyState message="İşler yüklenemedi" suggestion="Lütfen daha sonra tekrar deneyin." />
  );

  let body;
  if (view === "settings") {
    body = <TaskSettings projects={projects} />;
  } else if (view === "board") {
    if (boardQuery.isError) {
      body = errorNode;
    } else if (boardQuery.isLoading) {
      body = <ListSkeleton variant="cards" message="Pano yükleniyor..." />;
    } else {
      body = (
        <div className="relative flex-1 min-h-0 flex flex-col">
          {boardQuery.isFetching && <MascotLoadingOverlay message="Pano yükleniyor..." />}
          <TaskBoard
            columns={boardQuery.data?.data?.columns || []}
            showCancelled={filters.includeCancelled}
            // Tüm projelerde sütun içi sıra otomatik; yalnız sütunlar arası taşıma var.
            reorderable={boardQuery.data?.data?.ordered !== false}
            onMove={onMove}
            onSelect={(t) => openTask(t.key)}
          />
        </div>
      );
    }
  } else if (listQuery.isError) {
    body = errorNode;
  } else {
    body = (
      <div className="relative flex-1 min-h-0 flex flex-col">
        {listQuery.isFetching && !listQuery.isLoading && <MascotLoadingOverlay message="İşler yükleniyor..." />}
        <TaskList
        tasks={listData?.tasks || []}
        total={listData?.total || 0}
        page={filters.page}
        onPage={(page) => setFilters((prev) => ({ ...prev, page }))}
        sort={filters.sort}
        onSort={(sort) => setFilter({ sort })}
        onSelect={(t) => openTask(t.key)}
        loading={listQuery.isLoading}
        childArgs={{ token, project: activeProject || null, ...filterArgs }}
        />
      </div>
    );
  }

  const multi = (placeholder, options, key) => (
    <CustomDropdown
      multi
      compact
      className="w-40"
      placeholder={placeholder}
      options={options}
      value={pick(options, filters[key])}
      onChange={(sel) => setFilter({ [key]: sel.map((o) => o.id) })}
    />
  );

  return (
    <PageShell>
      <div className="px-3 pt-3 sm:px-4 md:px-6 md:pt-6">
        <PageHeader
          icon={FaTasks}
          iconColor="blue"
          title="İş Planlama"
          stats={headerStats}
          isLoading={listQuery.isLoading}
          actions={
            canCreate
              ? [{ label: "Yeni İş", icon: FaPlus, variant: "primary", onClick: () => setNewOpen(true) }]
              : []
          }
        />
      </div>

      <div className="px-3 pt-3 sm:px-4 md:px-6 space-y-2 shrink-0">
        <FilterTabs
          filters={tabs}
          activeFilter={view}
          onChange={(id) => setFilter({ view: id })}
        />
        {view !== "settings" && (
          <div className="flex flex-wrap items-center gap-2 pb-2">
            <CustomDropdown
              compact
              className="w-44"
              placeholder="Proje"
              options={projectOptions}
              allOption={ALL_PROJECTS}
              value={
                activeProject
                  ? projectOptions.find((o) => o.id === activeProject) || null
                  : ALL_PROJECTS
              }
              onChange={(o) => setFilter({ project: o && o.id !== "all" ? o.id : "" })}
            />
            <SearchInput
              placeholder="Başlık veya anahtar ara"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              wrapperClassName="w-56"
            />
            {multi("Atanan", assigneeOptions, "assignee")}
            {multi("Öncelik", priorityOptions, "priority")}
            {multi("Tür", typeOptions, "type")}
            {multi("Etiket", labelOptions, "labels")}
            <Checkbox
              label="Sadece benimkiler"
              className="gap-2"
              checked={filters.mine}
              onChange={(e) => setFilter({ mine: e.target.checked })}
            />
            {!archiveMode && (
              <Checkbox
                label="İptalleri göster"
                className="gap-2"
                checked={filters.includeCancelled}
                onChange={(e) => setFilter({ includeCancelled: e.target.checked })}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 flex flex-col">{body}</div>

      <TaskDetailDrawer
        taskKey={selectedKey}
        open={Boolean(selectedKey)}
        onClose={closeTask}
        onOpenTask={openTask}
      />
      <NewTaskModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        defaultProjectId={activeProject || undefined}
        onCreated={(task) => {
          setNewOpen(false);
          if (task?.key) openTask(task.key);
        }}
      />
    </PageShell>
  );
}
